/**
 * Analytics Service for Nasi iSpani GCRA Training Portal
 * Tracks user interactions and course engagement for program planning insights
 */

class AnalyticsService {
  constructor() {
    this.baseUrl = '/api/analytics';
    this.sessionId = this.generateSessionId();
    this.pageStartTime = null;
    this.sectionsViewed = new Set();
    this.maxScrollDepth = 0;
    this.eventQueue = [];
    this.isTracking = true;

    // Initialize tracking
    this.initializeTracking();
  }

  /**
   * Generate a unique session ID for anonymous tracking
   */
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Initialize tracking features
   */
  initializeTracking() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushEventQueue();
      }
    });

    // Track page unload
    window.addEventListener('beforeunload', () => {
      this.flushEventQueue();
    });

    // Batch send events every 30 seconds
    setInterval(() => {
      this.flushEventQueue();
    }, 30000);
  }

  /**
   * Track generic page view
   */
  trackPageView(pageName, metadata = {}) {
    if (!this.isTracking) return;

    const eventData = {
      page_name: pageName,
      page_url: window.location.href,
      referrer_url: document.referrer,
      timestamp: Date.now(),
      ...metadata
    };

    this.queueEvent('event', eventData);
  }

  /**
   * Track course page view
   */
  trackCoursePageView(courseId, referrerUrl = '') {
    if (!this.isTracking) return;

    this.pageStartTime = Date.now();
    this.sectionsViewed.clear();
    this.maxScrollDepth = 0;

    // Set up scroll tracking
    this.setupScrollTracking();

    const eventData = {
      course: courseId,
      referrer_url: referrerUrl,
      sections_viewed: [],
      scroll_depth_percentage: 0
    };

    this.queueEvent('page-view', eventData);
  }

  /**
   * Track course page exit with duration and engagement metrics
   */
  trackCoursePageExit(courseId) {
    if (!this.isTracking || !this.pageStartTime) return;

    const duration = Math.floor((Date.now() - this.pageStartTime) / 1000);
    
    const eventData = {
      course: courseId,
      duration_seconds: duration,
      sections_viewed: Array.from(this.sectionsViewed),
      scroll_depth_percentage: this.maxScrollDepth
    };

    this.queueEvent('page-view', eventData);
    this.flushEventQueue(); // Send immediately on page exit
  }

  /**
   * Track user interaction
   */
  trackInteraction(type, courseId = null, metadata = {}) {
    if (!this.isTracking) return;

    const eventData = {
      interaction_type: type,
      metadata: metadata
    };

    if (courseId) {
      eventData.course = courseId;
    }

    this.queueEvent('interaction', eventData);
  }

  /**
   * Track section view (modules, requirements, careers, etc.)
   */
  trackSectionView(sectionName, courseId = null) {
    if (!this.isTracking) return;

    this.sectionsViewed.add(sectionName);
    
    this.trackInteraction('section_viewed', courseId, {
      section: sectionName,
      timestamp: Date.now()
    });
  }

  /**
   * Track wishlist actions
   */
  trackWishlistAction(action, courseId, courseName) {
    if (!this.isTracking) return;

    const interactionType = action === 'add' ? 'wishlist_add' : 'wishlist_remove';
    
    this.trackInteraction(interactionType, courseId, {
      course_name: courseName,
      action: action
    });
  }

  /**
   * Track application actions
   */
  trackApplicationAction(action, courseId, courseName) {
    if (!this.isTracking) return;

    const interactionType = action === 'start' ? 'application_start' : 'application_submit';
    
    this.trackInteraction(interactionType, courseId, {
      course_name: courseName,
      action: action
    });
  }

  /**
   * Track course search and filtering
   */
  trackCourseSearch(searchTerm, filters = {}) {
    if (!this.isTracking) return;

    this.trackInteraction('course_search', null, {
      search_term: searchTerm,
      filters: filters,
      results_count: filters.results_count || 0
    });
  }

  /**
   * Track external link clicks (like SAQA links)
   */
  trackExternalLinkClick(linkType, url, courseId = null) {
    if (!this.isTracking) return;

    this.trackInteraction('external_link_click', courseId, {
      link_type: linkType,
      url: url
    });
  }

  /**
   * Set up scroll depth tracking
   */
  setupScrollTracking() {
    let ticking = false;

    const updateScrollDepth = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      if (scrollPercent > this.maxScrollDepth) {
        this.maxScrollDepth = Math.min(scrollPercent, 100);
      }
      
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollDepth);
        ticking = true;
      }
    };

    // Remove existing scroll listener if any
    window.removeEventListener('scroll', this.scrollHandler);
    
    // Add new scroll listener
    this.scrollHandler = onScroll;
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  /**
   * Queue an event for batch sending
   */
  queueEvent(endpoint, data) {
    this.eventQueue.push({
      endpoint: endpoint,
      data: {
        session_id: this.sessionId,
        ...data
      },
      timestamp: Date.now()
    });

    // If queue gets too large, flush immediately
    if (this.eventQueue.length >= 10) {
      this.flushEventQueue();
    }
  }

  /**
   * Send queued events to the backend
   */
  async flushEventQueue() {
    if (this.eventQueue.length === 0) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // Group events by endpoint
      const groupedEvents = eventsToSend.reduce((groups, event) => {
        if (!groups[event.endpoint]) {
          groups[event.endpoint] = [];
        }
        groups[event.endpoint].push(event.data);
        return groups;
      }, {});

      // Send each group
      const promises = Object.entries(groupedEvents).map(([endpoint, events]) => {
        return this.sendEvents(endpoint, events);
      });

      await Promise.all(promises);
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
      // Re-queue events if they failed to send
      this.eventQueue.unshift(...eventsToSend);
    }
  }

  /**
   * Send events to a specific endpoint
   */
  async sendEvents(endpoint, events) {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }

    // Send events individually for now (could be optimized to batch)
    const promises = events.map(eventData => {
      return fetch(`${this.baseUrl}/track/${endpoint}/`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(eventData)
      });
    });

    return Promise.all(promises);
  }

  /**
   * Enable/disable tracking (for privacy compliance)
   */
  setTrackingEnabled(enabled) {
    this.isTracking = enabled;
    
    if (!enabled) {
      // Clear any queued events
      this.eventQueue = [];
      // Remove scroll listener
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
    }
  }

  /**
   * Get current tracking status
   */
  isTrackingEnabled() {
    return this.isTracking;
  }

  /**
   * Track generic custom event
   */
  trackCustomEvent(category, action, label = '', value = null, customData = {}) {
    if (!this.isTracking) return;

    const eventData = {
      event_category: category,
      event_action: action,
      event_label: label,
      event_value: value,
      page_url: window.location.href,
      referrer_url: document.referrer,
      custom_data: customData
    };

    this.queueEvent('event', eventData);
  }
}

// Create and export a singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
